import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Check, CheckCheck, X } from "lucide-react";
import { cn } from "@/lib/utils";

export type SuggestionField =
  | "stem"
  | "option_a"
  | "option_b"
  | "option_c"
  | "option_d"
  | "option_e"
  | "explanation";

export interface FieldSuggestion {
  field: SuggestionField;
  current: string;
  suggested: string;
  rationale?: string;
}

interface AISuggestionsDiffProps {
  suggestions: FieldSuggestion[];
  appliedFields: Set<SuggestionField>;
  onApply: (field: SuggestionField, value: string) => void;
  onReject: (field: SuggestionField) => void;
  onApplyAll?: () => void;
  className?: string;
}

const FIELD_LABEL: Record<SuggestionField, string> = {
  stem: "Stem",
  option_a: "Option A",
  option_b: "Option B",
  option_c: "Option C",
  option_d: "Option D",
  option_e: "Option E",
  explanation: "Explanation",
};

export function AISuggestionsDiff({
  suggestions,
  appliedFields,
  onApply,
  onReject,
  onApplyAll,
  className,
}: AISuggestionsDiffProps) {
  const filtered = useMemo(
    () =>
      suggestions.filter(
        (s) => (s.suggested ?? "").trim() && s.suggested.trim() !== (s.current ?? "").trim()
      ),
    [suggestions]
  );

  if (filtered.length === 0) return null;

  return (
    <Card className={cn("border-primary/30 bg-primary/[0.03]", className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            AI-suggested edits
            <Badge variant="secondary" className="text-[10px]">
              {filtered.length}
            </Badge>
          </CardTitle>
          {onApplyAll && filtered.some((s) => !appliedFields.has(s.field)) && (
            <Button size="sm" variant="default" onClick={onApplyAll}>
              <CheckCheck className="h-4 w-4 mr-1" />
              Apply all
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {filtered.map((s) => {
          const applied = appliedFields.has(s.field);
          return (
            <div
              key={s.field}
              className={cn(
                "rounded-lg border p-3 transition-colors",
                applied
                  ? "border-success/40 bg-success/5"
                  : "border-border bg-background"
              )}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">
                    {FIELD_LABEL[s.field]}
                  </Badge>
                  {applied && (
                    <span className="text-[10px] text-success font-medium flex items-center gap-1">
                      <Check className="h-3 w-3" /> Applied
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {!applied && (
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => onApply(s.field, s.suggested)}
                      className="h-7 px-2 text-xs"
                    >
                      <Check className="h-3 w-3 mr-1" />
                      Apply
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onReject(s.field)}
                    className="h-7 px-2 text-xs"
                  >
                    <X className="h-3 w-3 mr-1" />
                    {applied ? "Undo" : "Reject"}
                  </Button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    Current
                  </p>
                  <div className="rounded-md border border-border bg-muted/30 p-2 whitespace-pre-wrap text-foreground/90">
                    {s.current || (
                      <span className="text-muted-foreground italic">empty</span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-primary mb-1">
                    AI suggestion
                  </p>
                  <div className="rounded-md border border-primary/30 bg-primary/5 p-2 whitespace-pre-wrap text-foreground">
                    {s.suggested}
                  </div>
                </div>
              </div>
              {s.rationale && (
                <p className="text-xs text-muted-foreground mt-2 italic">
                  {s.rationale}
                </p>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
